import apiFetch from '../../../utils/apiFetch';
import { rollDiceWithBox } from '../../../utils/diceBoxManager';
import { applyBardicInspirationUpdate, withBardicDie } from '../../../shared/rules/bard.mjs';

const rollDie = async (sides) => {
  const roll = await rollDiceWithBox([{ count: 1, sides }]).catch(() => null);
  const rolled = Number(roll?.rolls?.[0]?.[0]);
  return Number.isInteger(rolled) && rolled >= 1 && rolled <= sides ? rolled : Math.floor(Math.random() * sides) + 1;
};

/** Spends the held die on one d20 test made on the sheet, with the roll made; the same requestId replays. */
export const claimBardicDie = async (characterId, { requestId, kind, rolls }) => {
  const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/bardic-inspiration`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId, kind, rolls }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || 'Bardic Inspiration could not be used.');
  return result;
};

/**
 * The held die on a roll made on the sheet: rolls it in the dice box, spends it on the server with that roll and returns
 * `{ rolls, bonus, sides }` (a replayed claim returns the roll stored with the first one).
 */
export async function spendBardicDieOnRoll({ character, characterId, requestId, kind, onCharacterChange }) {
  const sides = Number(character?.bardicInspiration?.sides) || 6;
  const fresh = await rollDie(sides);
  const claimed = await claimBardicDie(characterId, { requestId, kind, rolls: [fresh] });
  onCharacterChange?.(previous => withBardicDie(previous, claimed.bardicInspiration));
  const rolls = Array.isArray(claimed.bardicInspiration?.rolls) && claimed.bardicInspiration.rolls.length === 1 ? claimed.bardicInspiration.rolls : [fresh];
  return { rolls, bonus: rolls[0], sides };
}

/**
 * Peerless Skill on a roll made on the sheet: rolls the Bard's die, and `worked(bonus)` says whether the roll now succeeds
 * (it can ask the player when no DC is known). Only then is the use spent. Returns `{ rolls, bonus, sides, spent }`.
 */
export async function spendPeerlessSkillOnRoll({ characterId, requestId, kind, sides, worked, onCharacterChange }) {
  const rolled = await rollDie(sides);
  if (!await worked(rolled)) return { rolls: [rolled], bonus: rolled, sides, spent: false };
  const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/bard/peerless-skill`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId, kind, rolls: [rolled] }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || 'Peerless Skill could not be used.');
  onCharacterChange?.(previous => applyBardicInspirationUpdate(previous, result.inspiration));
  return { rolls: result.rolls || [rolled], bonus: (result.rolls || [rolled])[0], sides, spent: true };
}
